// =====================================================
// Rate Limiter Hook
// AdminMunicipal v6.0 - Security Enhancement
// Prevents brute force attacks on authentication
// =====================================================

/**
 * SECURITY NOTE: This is a CLIENT-SIDE rate limiter for UX enhancement only.
 * It can be bypassed by clearing sessionStorage.
 *
 * Server-side protection is provided by:
 * 1. Supabase Auth's built-in rate limiting
 * 2. Cloudflare's DDoS protection (if enabled)
 *
 * See docs/RATE_LIMITING.md for complete architecture documentation.
 */

import { useState, useRef, useCallback } from 'react';

interface RateLimiterConfig {
  /** Maximum number of attempts before lockout */
  maxAttempts: number;
  /** Time window in milliseconds for tracking attempts */
  windowMs: number;
  /** Lockout duration in milliseconds after max attempts reached */
  lockoutMs: number;
}

interface RateLimiterState {
  /** Whether the user can make another attempt */
  canAttempt: boolean;
  /** Whether the user is currently locked out */
  isLocked: boolean;
  /** Number of remaining attempts */
  remainingAttempts: number;
  /** Time remaining on lockout in milliseconds (0 if not locked) */
  remainingLockTime: number;
  /** Current number of failed attempts */
  attempts: number;
}

interface RateLimiterActions {
  /** Record a login attempt (success or failure) */
  recordAttempt: (success: boolean) => void;
  /** Reset the rate limiter */
  reset: () => void;
  /** Get formatted time remaining for display */
  getFormattedLockTime: () => string;
}

type UseRateLimiterReturn = RateLimiterState & RateLimiterActions;

const DEFAULT_CONFIG: RateLimiterConfig = {
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000,  // 15 minutes
  lockoutMs: 30 * 60 * 1000, // 30 minutes lockout
};

/**
 * Rate limiter hook to prevent brute force attacks.
 *
 * Features:
 * - Tracks failed login attempts
 * - Implements lockout after max attempts
 * - Auto-resets after time window expires
 * - Persists state in sessionStorage (cleared on tab close)
 *
 * @param config - Configuration options
 * @returns Rate limiter state and actions
 *
 * @example
 * ```tsx
 * const {
 *   canAttempt,
 *   isLocked,
 *   remainingAttempts,
 *   recordAttempt,
 *   getFormattedLockTime,
 * } = useRateLimiter();
 *
 * const handleLogin = async () => {
 *   if (!canAttempt) {
 *     setError(`Demasiados intentos. Intenta en ${getFormattedLockTime()}`);
 *     return;
 *   }
 *
 *   try {
 *     await signIn(email, password);
 *     recordAttempt(true); // Success resets the counter
 *   } catch (error) {
 *     recordAttempt(false); // Failure increments the counter
 *   }
 * };
 * ```
 */
export function useRateLimiter(config: Partial<RateLimiterConfig> = {}): UseRateLimiterReturn {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  // Load initial state from sessionStorage
  const getInitialState = (): { attempts: number; windowStart: number; lockedUntil: number | null } => {
    try {
      const stored = sessionStorage.getItem('rateLimiterState');
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate stored data
        if (
          typeof parsed.attempts === 'number' &&
          typeof parsed.windowStart === 'number'
        ) {
          return parsed;
        }
      }
    } catch {
      // Ignore parse errors
    }
    return { attempts: 0, windowStart: Date.now(), lockedUntil: null };
  };

  const initialState = getInitialState();

  const [attempts, setAttempts] = useState(initialState.attempts);
  const [lockedUntil, setLockedUntil] = useState<number | null>(initialState.lockedUntil);
  const windowStart = useRef(initialState.windowStart);

  // Persist state to sessionStorage
  const persistState = useCallback((newAttempts: number, newLockedUntil: number | null) => {
    try {
      sessionStorage.setItem('rateLimiterState', JSON.stringify({
        attempts: newAttempts,
        windowStart: windowStart.current,
        lockedUntil: newLockedUntil,
      }));
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Check if currently locked
  const checkIsLocked = useCallback((): boolean => {
    if (lockedUntil && Date.now() < lockedUntil) {
      return true;
    }
    // Clear expired lockout
    if (lockedUntil && Date.now() >= lockedUntil) {
      setLockedUntil(null);
      setAttempts(0);
      windowStart.current = Date.now();
      persistState(0, null);
    }
    return false;
  }, [lockedUntil, persistState]);

  // Check if user can make another attempt
  const checkCanAttempt = useCallback((): boolean => {
    if (checkIsLocked()) {
      return false;
    }

    // Reset window if expired
    if (Date.now() - windowStart.current > mergedConfig.windowMs) {
      windowStart.current = Date.now();
      setAttempts(0);
      persistState(0, null);
    }

    return attempts < mergedConfig.maxAttempts;
  }, [attempts, checkIsLocked, mergedConfig.maxAttempts, mergedConfig.windowMs, persistState]);

  // Record a login attempt
  const recordAttempt = useCallback((success: boolean) => {
    if (success) {
      // Success resets the counter
      setAttempts(0);
      setLockedUntil(null);
      windowStart.current = Date.now();
      persistState(0, null);
      return;
    }

    // Failure increments the counter
    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    // Check if we should lock
    if (newAttempts >= mergedConfig.maxAttempts) {
      const newLockUntil = Date.now() + mergedConfig.lockoutMs;
      setLockedUntil(newLockUntil);
      persistState(newAttempts, newLockUntil);
    } else {
      persistState(newAttempts, null);
    }
  }, [attempts, mergedConfig.maxAttempts, mergedConfig.lockoutMs, persistState]);

  // Reset the rate limiter
  const reset = useCallback(() => {
    setAttempts(0);
    setLockedUntil(null);
    windowStart.current = Date.now();
    persistState(0, null);
  }, [persistState]);

  // Get remaining lock time
  const getRemainingLockTime = useCallback((): number => {
    if (!lockedUntil) return 0;
    return Math.max(0, lockedUntil - Date.now());
  }, [lockedUntil]);

  // Get formatted lock time for display
  const getFormattedLockTime = useCallback((): string => {
    const remaining = getRemainingLockTime();
    if (remaining === 0) return '';

    const minutes = Math.ceil(remaining / 60000);
    if (minutes === 1) {
      return '1 minuto';
    }
    return `${minutes} minutos`;
  }, [getRemainingLockTime]);

  // Calculate remaining attempts
  const remainingAttempts = Math.max(0, mergedConfig.maxAttempts - attempts);

  return {
    canAttempt: checkCanAttempt(),
    isLocked: checkIsLocked(),
    remainingAttempts,
    remainingLockTime: getRemainingLockTime(),
    attempts,
    recordAttempt,
    reset,
    getFormattedLockTime,
  };
}
